import { Checkbox, Chip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { getAccountingBolList, updateBolStatus } from '../../api/bolApi';
import { getStores } from '../../api/storeApi';
import BolFilterModal from '../../components/Bol/BolFilterModal';
import BolWorkspace from '../../components/Bol/BolWorkspace';
import AppDialogMessage from '../../components/Common/AppDialogMessage';
import ViewFileModal from '../../components/Common/ViewFileModal';
import GenerateInvoice from '../../components/Invoice/GenerateInvoice';
import BolPrint from '../../components/Print/BolPrint';
import BolReportDownload from '../../components/Print/BolReport';
import { useAppDialog } from '../../Hooks/useAppDialog';
import { useDebounce } from '../../Hooks/useDebounce';
import { getDayOfWeek } from '../../Utils/getDaybyDate';

const initialFilter = {
  shipper: '',
  receiver: '',
  po_no: '',
  // bol_no: '',
  type: '',
  invoice_no: '',
  vendor_delivered: null as boolean | null,
  dc_received: null as boolean | null,
  approved: null as boolean | null,
  payment_status: null as boolean | null,
  trailer: '',
  supplier: '',
  start_date: '',
  end_date: '',
  info: '',
};

const SuperAdminBOL: React.FC = () => {
  const [bolList, setBolList] = useState<any[]>([]);
  const [storeList, setStoreList] = useState<any[]>([]);
  const [filterModal, setFilterModal] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [filterDraft, setFilterDraft] = useState(initialFilter);
  const [appliedFilter, setAppliedFilter] = useState(initialFilter);
  const { dialog, openDialog, closeDialog } = useAppDialog();
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 1,
    totalRows: 0,
  });

  const [viewFileModal, setViewFileModal] = useState({
    open: false,
    filePath: null as string | null,
    title: 'View File',
  });

  /**
   * Fetch stores once.
   * Used by VendorReport and any future store-aware rendering.
   */
  useEffect(() => {
    getStores()
      .then((data) => setStoreList(data))
      .catch((error) => console.log(error));
  }, []);

  /**
   * Fetch accounting-side BOL list.
   * Keeps the same existing API route and filtering behavior.
   */
  const fetchBolList = async (page: number, limit: number) => {
    try {
      const result = await getAccountingBolList({
        page,
        limit,
        bol_no: search.trim(),
        filters: appliedFilter,
      });

      setBolList(result.data || []);

      setPagination((prev) => ({
        ...prev,
        page,
        total: Math.max(1, Math.ceil((result.totalRows || 0) / prev.limit)),
        totalRows: result.totalRows || 0,
      }));
    } catch (error) {
      console.log('Error in getAccountingBolList:', error);
    }
  };

  /**
   * Refetch when page, search, or applied filters change.
   */
  useEffect(() => {
    fetchBolList(pagination.page, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, debouncedSearch, appliedFilter]);

  /**
   * Top bar search handler.
   * Uses existing backend filter key: bol_no.
   */
  const handleSearch = (value: string) => {
    setSearch(value);
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  /**
   * Update filter modal draft only.
   */
  const handleFilterChange = (key: string, value: any) => {
    setFilterDraft((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /**
   * Apply modal draft filters.
   */
  const handleFilterApply = () => {
    setAppliedFilter(filterDraft);
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
    setFilterModal(false);
  };

  /**
   * Reset all active filters and search.
   */
  const handleResetAll = () => {
    setSearch('');
    setFilterDraft(initialFilter);
    setAppliedFilter(initialFilter);
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
    setFilterModal(false);
  };

  /**
   * Open uploaded file preview modal.
   */
  const handleOpenFileModal = (filePath: string, title?: string) => {
    setViewFileModal({
      open: true,
      filePath,
      title: title || 'View File',
    });
  };

  /**
   * Update payment_status from accounting side.
   * Keeps the existing API call and confirmation flow.
   */
  const handlePaymentApproval = (status: boolean | null, bol_no: string) => {
    const confirmMessage = !status
      ? `Are you sure you want to reject payment for ${bol_no}?`
      : `Are you sure you issued payment for ${bol_no}?`;

    openDialog({
      variant: 'warning',
      title: 'Confirm Payment Update',
      message: confirmMessage,
      confirmText: status ? 'Approve' : 'Reject',
      cancelText: 'Cancel',

      onConfirm: async () => {
        try {
          const result = await updateBolStatus({
            bol_no,
            status,
            column: 'payment_status',
          });

          setBolList((prev) =>
            prev.map((bol: any) =>
              bol.bol_no === bol_no
                ? { ...bol, payment_status: result.data?.[0]?.payment_status }
                : bol
            )
          );

          openDialog({
            variant: status ? 'success' : 'warning',
            title: status ? 'Payment Approved' : 'Payment Rejected',
            message:
              result.message || 'Payment status updated successfully.',
            confirmText: 'Close',
            hideCancel: true,
          });
        } catch (error: any) {
          console.log('Error in approving payment:', error);

          openDialog({
            variant: 'error',
            title: 'Server Error',
            message:
              error.message ||
              'An unexpected error occurred while updating payment status.',
            confirmText: 'Close',
            hideCancel: true,
          });
        }
      },
    });
  };
  // const handlePaymentApproval = async (status: boolean | null, bol_no: string) => {
  //   let message = '';
  //   !status
  //     ? (message = `Are you sure you want to reject payment for ${bol_no}?`)
  //     : (message = `Are you sure you issued payment for ${bol_no}?`);

  //   const confirm = window.confirm(message);
  //   if (!confirm) return;

  //   try {
  //     const response = await fetch(`${URL_PREFIX}/bol/updateStatus`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         Authorization: `Bearer ${token}`,
  //       },
  //       body: JSON.stringify({
  //         status,
  //         bol_no,
  //         column: 'payment_status',
  //       }),
  //     });

  //     const { data, message } = await response.json();

  //     if (response.status === 200) {
  //       setBolList((prev) =>
  //         prev.map((bol: any) =>
  //           bol.bol_no === bol_no
  //             ? { ...bol, payment_status: data?.[0]?.payment_status }
  //             : bol
  //         )
  //       );
  //       // IF the payment status is updated to true, show success dialog. If updated to false, show error dialog since payment cannot be rejected once issued.
  //       openDialog({
  //         variant: status ? 'success' : 'error',
  //         title: status ? 'Payment Approved' : 'Payment Rejected',
  //         message: message || 'Payment status updated successfully.',
  //         confirmText: 'Close',
  //         hideCancel: true,
  //       });
  //       return;
  //     }

  //     openDialog({
  //       variant: 'error',
  //       title: 'Update Failed',
  //       message: message || 'Something went wrong while updating payment status.',
  //       confirmText: 'Close',
  //       hideCancel: true,
  //     });
  //   } catch (error) {
  //     console.log('Error in approving payment:', error);
  //   }
  // };

  /**
   * Shared formatter for shipper / receiver.
   */
  const formatShortLocation = (party: any) =>
    `${party?.name || ''}${party?.postal_code ? ` (${party.postal_code.split(',')[0]})` : ''}`;

  /**
   * Shared date format.
   */
  const formatDate = (value?: string) =>
    value ? `${value.split('T')[0]} ${getDayOfWeek(value) ?? ''}` : '';

  /**
   * Accounting page columns.
   */
  const columns = useMemo(
    () => [
      {
        key: 'shipper',
        label: 'Shipper',
        render: (row: any) => formatShortLocation(row.shipper),
      },
      {
        key: 'receiver',
        label: 'Receiver',
        render: (row: any) => formatShortLocation(row.receiver),
      },
      {
        key: 'bol_date',
        label: 'BOL Date',
        render: (row: any) => formatDate(row.bol_date),
      },
      {
        key: 'amount',
        label: 'Rate',
        render: (row: any) => `$${row?.amount ?? ''}`,
      },
      {
        key: 'dclocation',
        label: 'Location',
      },
      {
        key: 'vendor_delivered',
        label: 'G200 Status',
        render: (row: any) => (
          <Chip
            size="small"
            label={row?.vendor_delivered ? 'DELIVERED' : 'PENDING'}
            color={row?.vendor_delivered ? 'success' : 'warning'}
            variant="outlined"
          />
        ),
      },
      {
        key: 'dc_received',
        label: 'DC Status',
        render: (row: any) => (
          <Chip
            size="small"
            label={row?.dc_received ? 'RECEIVED' : 'PENDING'}
            color={row?.dc_received ? 'info' : 'warning'}
            variant="outlined"
          />
        ),
      },
      {
        key: 'bol_no',
        label: 'BOL No.',
        render: (row: any) => (
          <BolPrint bol={row} size="xs" variant="outline" textElement={row?.bol_no} />
        ),
      },
      {
        key: 'trailer',
        label: 'Carrier',
      },
      {
        key: 'invoice_no',
        label: 'Invoice',
        render: (row: any) =>
          row?.invoice_no ? (
            <GenerateInvoice bol={row} textElement={row.invoice_no} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              N/A
            </Typography>
          ),
      },
      {
        key: 'invoice_file',
        label: 'Proof of BOL',
        render: (row: any) =>
          row?.invoice_file ? (
            <Typography
              variant="body2"
              sx={{
                cursor: 'pointer',
                fontWeight: 700,
                textDecoration: 'underline',
              }}
              onClick={() =>
                handleOpenFileModal(`/uploads/${row.invoice_file}`, 'Proof of BOL')
              }
            >
              View
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              N/A
            </Typography>
          ),
      },
      {
        key: 'approval',
        label: 'Approve',
        render: (row: any) => (
          <Chip
            size="small"
            label={row?.approval ? 'APPROVED' : 'PENDING'}
            color={row?.approval ? 'success' : 'warning'}
            variant="outlined"
          />
        ),
      },
      {
        key: 'payment_status',
        label: 'Payment Status',
        render: (row: any) => {
          const canChangePayment =
            !row?.cancel &&
            !!row?.invoice_no &&
            !!row?.dc_received &&
            !!row?.vendor_delivered &&
            !!row?.approval;

          if (!canChangePayment) {
            return (
              <Typography variant="body2" color="text.secondary">
                N/A
              </Typography>
            );
          }

          return (
            <Checkbox
              checked={!!row?.payment_status}
              onChange={(event) =>
                handlePaymentApproval(event.target.checked, row.bol_no)
              }
            />
          );
        },
      },
    ],
    []
  );

  /**
   * Optional row styling for table.
   */
  const getRowSx = (row: any) => {
    if (row.cancel) {
      return {
        backgroundColor: '#f5f5f5',
        '& td': {
          color: '#8c8c8c',
        },
      };
    }

    if (row?.dc_received && row?.vendor_delivered && row?.payment_status) {
      return {
        backgroundColor: '#f8fff8',
      };
    }

    return {};
  };

  /**
   * Optional card styling for mobile.
   */
  const getCardSx = (row: any) => {
    if (row.cancel) {
      return {
        backgroundColor: '#f5f5f5',
        '& *': {
          color: '#8c8c8c',
        },
      };
    }

    if (row?.dc_received && row?.vendor_delivered && row?.payment_status) {
      return {
        backgroundColor: '#f8fff8',
      };
    }

    return {};
  };

  /**
   * Mobile / row actions.
   * Accounting can only view proof and toggle payment if allowed.
   */
  const renderRowActions = (row: any) => {
    const canChangePayment =
      !row?.cancel &&
      !!row?.invoice_no &&
      !!row?.dc_received &&
      !!row?.vendor_delivered &&
      !!row?.approval;

    return (
      <>
        {row?.invoice_file ? (
          <Typography
            variant="body2"
            sx={{
              cursor: 'pointer',
              fontWeight: 700,
              textDecoration: 'underline',
            }}
            onClick={() =>
              handleOpenFileModal(`/uploads/${row.invoice_file}`, 'Proof of BOL')
            }
          >
            View Proof
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No Proof
          </Typography>
        )}

        {/* {canChangePayment ? (
          <Checkbox
            checked={!!row?.payment_status}
            onChange={(event) =>
              handlePaymentApproval(event.target.checked, row.bol_no)
            }
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Payment N/A
          </Typography>
        )} */}
      </>
    );
  };

  return (
    <>
      <BolFilterModal
        open={filterModal}
        onClose={() => setFilterModal(false)}
        filters={filterDraft}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleResetAll}
      />

      <ViewFileModal
        open={viewFileModal.open}
        onClose={() =>
          setViewFileModal({
            open: false,
            filePath: null,
            title: 'View File',
          })
        }
        filePath={viewFileModal.filePath}
        title={viewFileModal.title}
      />

      <BolWorkspace
        data={bolList}
        columns={columns}
        pagination={pagination}
        setPagination={setPagination}
        search={search}
        setSearch={handleSearch}
        allowLocation={false}
        showFilterButton
        onOpenFilter={() => setFilterModal(true)}
        showCreateButton={false}
        // rowActions={renderRowActions}
        getRowSx={getRowSx}
        getCardSx={getCardSx}
        bottomRightSlot={<BolReportDownload />}
        showResetButton
        onResetFilters={handleResetAll}
      />
      <AppDialogMessage
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        variant={dialog.variant}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        hideCancel={dialog.hideCancel}
        onConfirm={dialog.onConfirm}
        onClose={closeDialog}
      />
    </>
  );
};

export default SuperAdminBOL;